import { Grid } from '@material-ui/core';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { BaseProps } from '../../../../common/BaseProps';
import {
  currencyFormat,
  formatarCep,
  formatarCpf,
  formatarNit,
} from '../../../../common/formatadores';
import Accordion, { SCROLL_OFFSET_NAV_FIXED } from '../../../../components/Accordion/Accordion';
import Card from '../../../../components/Card/Card';
import FieldGroup from '../../../../components/FieldGroup/FieldGroup';
import Message from '../../../../components/Message/Message';
import { selectPerfilCompleto } from '../../../Perfil/perfilSlice';
import {
  selectPessoa,
  selectRepresentanteLegal,
  selectResponsaveisFamiliares,
  selectMembrosFamiliares,
  irParaCarteiraDocumentos,
} from '../../consultaCompletaSlice';
import { TipoPerfil } from '../../../../model/TipoPerfil';
import React from 'react';

interface Props extends BaseProps {
  styles: any;
  view: DetalhesView;
}

export enum DetalhesView {
  MEMBRO = 'MEMBRO',
  RESPONSAVEL = 'RESPONSAVEL',
  REPRESENTANTE_LEGAL = 'REPRESENTANTE_LEGAL',
}

const DetalhesMembro: React.FC<Props> = (props) => {
  const { styles, view } = props;
  const dispatch = useAppDispatch();

  const perfil = useAppSelector(selectPerfilCompleto);
  const pessoa = useAppSelector(selectPessoa);
  const responsaveisFamiliares = useAppSelector(selectResponsaveisFamiliares);
  const representanteLegal = useAppSelector(selectRepresentanteLegal);
  const membrosFamiliares = useAppSelector(selectMembrosFamiliares);

  const dadosCadastro = pessoa?.pessoaDadosCadastroDTO;

  const trabalhoRenda = pessoa?.pessoaTrabalhoRendaDTO;
  const escolaridade = pessoa?.pessoaEscolaridadeDTO;
  const deficiencia = pessoa?.pessoaDeficienciaDTO;
  const carteiraDocumentos = pessoa?.carteiraDocumentos;

  const possuiCarteiraDocumentos = carteiraDocumentos?.documentos?.some(
    (doc: any) => doc.possuiDocumento === true
  );

  const exibirBotaoCarteiraDocumentos = (): boolean => {
    const isRF = perfil?.tipoPerfil?.codigo === TipoPerfil.RESPONSAVEL_FAMILIAR;
    if (isRF) {
      const rfNaLista = membrosFamiliares.find(m => m.isResponsavelFamiliar && m.identificador === perfil?.identificador);
      return !rfNaLista?.obito;
    }
    const membroVisualizado = membrosFamiliares.find(m => m.identificador === pessoa?.identificador);
    return !membroVisualizado?.obito;
  };

  const formatarValor = (value: number): string => {
    return value > 0 ? currencyFormat(value) : '';
  };

  const localNascimento = () => {
    if (dadosCadastro.tipoLocalNascimento === 3) {
      return dadosCadastro.nomePaisOrigem;
    }
    return `${dadosCadastro.nomeIbgeMunicipioNascimento} - ${dadosCadastro.siglaUFNascimento}`;
  };

  const montarEmissaoRG = (siglaOrgaoEmissor: string, siglaUfIdentidade: string): any => {
    return siglaOrgaoEmissor && siglaUfIdentidade
      ? `${siglaOrgaoEmissor} - ${siglaUfIdentidade}`
      : '-';
  };

  const mostrarParentesco = () => {
    if (pessoa?.tipoRelacaoParentescoRF?.nome === 'Pessoa Responsável pela Unidade Familiar - RF') {
      return 'Responsável Familiar - RF';
    }
    return pessoa?.tipoRelacaoParentescoRF?.nome;
  };

  const formatarLocalRegistro = () => {
    if (!dadosCadastro.municipioRegistroCertidao && !dadosCadastro.ufRegistroCertidao) {
      return null
    }
    let localRegistro = '';
    if (dadosCadastro.municipioRegistroCertidao) {
      localRegistro = dadosCadastro.municipioRegistroCertidao;
      if (dadosCadastro.ufRegistroCertidao) {
        localRegistro += ' - ' + dadosCadastro.ufRegistroCertidao;
      }
    } else {
      localRegistro = dadosCadastro.ufRegistroCertidao;
    }
    return localRegistro;
  }

  const mostrarTelefoneRF = (ddd: any, telefone: any) => {
    if (!ddd || !telefone) {
      return null;
    }
    return `(${ddd}) ${telefone}`;
  }

  const mostrarTelefoneRL = rl => {
    if (!rl.dddTelefone || !rl.numeroTelefone) {
      return null;
    }
    return `(${rl.dddTelefone}) ${rl.numeroTelefone}`;
  }

  const indicacaoRLDeObito = () => {
    return representanteLegal?.obito;
  };

  const mensagemObitoRL = () => {
    return (
      <>
        O representante legal está com registro de falecimento.
        <br />
        Alguma pessoa da família deve ir ao CRAS atualizar o Cadastro Único.
      </>
    );
  };

  const cardIdentificacao = (
    <>
      {dadosCadastro && (
        <Accordion
          id="identificacao"
          title="Identificação"
          icon="fa-id-badge"
          scroll
          scrollOffset={SCROLL_OFFSET_NAV_FIXED}
        >
          <div className={styles.cardContent}>
            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="NIS, PIS ou PASEP"
                value={
                  dadosCadastro.numeroNisPisPasepPessoa
                    ? formatarNit(dadosCadastro.numeroNisPisPasepPessoa)
                    : ''
                }
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Sexo" value={dadosCadastro.tipoSexoPessoa?.nome} />
              <FieldGroup label="Cor/raça" value={dadosCadastro.tipoRacaCor?.nome} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Data de nascimento"
                value={
                  dadosCadastro.dataNascimentoPessoa
                    ? moment(dadosCadastro.dataNascimentoPessoa).format('DD/MM/YYYY')
                    : null
                }
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Filiação 1 (ou nome completo da mãe)"
                value={dadosCadastro.filiacao1}
                emptyValue="Não sabe"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Filiação 2"
                value={dadosCadastro.filiacao2}
                emptyValue="Não sabe"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Local de nascimento" value={localNascimento()} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Nascimento registrado em cartório"
                value={dadosCadastro.tipoCertidaoRegistrada?.nome}
              />
            </div>
            {dadosCadastro.temIdentidadeGenero === 1 &&
              <div className={styles.fieldGroupRow}>
                <FieldGroup
                  label="Pessoa trans ou travesti?"
                  value={dadosCadastro.tipoGeneroIdentificacao?.nome}
                />
              </div>
            }

            {dadosCadastro.temIdentidadeGenero === 1 &&
              <div className={styles.fieldGroupRow}>
                <FieldGroup
                  label="Qual é o gênero/identidade de gênero?"
                  value={dadosCadastro.tipoIdentidadeGenero?.nome}
                />
              </div>
            }
          </div>
        </Accordion>
      )}
    </>
  );

  const cardDocumentos = (
    <>
      {dadosCadastro && (
        <Accordion
          id="documentos"
          title="Documentos"
          icon="fa-id-card"
          scroll
          scrollOffset={SCROLL_OFFSET_NAV_FIXED}
        >
          <div className={styles.cardContent}>
            {exibirBotaoCarteiraDocumentos() && (
              <div className={styles.fieldGroupRow}>
                <button
                  className={styles.botaoCarteiraDocumentos}
                  onClick={() => dispatch(irParaCarteiraDocumentos())}
                >
                  <i className="fas fa-id-card" />
                  Carteira de Documentos
                </button>
              </div>
            )}

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Tipo de certidão" value={dadosCadastro.tipoCertidaoCivil?.nome} />
              <FieldGroup
                label="Data do registro"
                value={
                  dadosCadastro.dataEmissaoCertidao
                    ? moment(dadosCadastro.dataEmissaoCertidao).format('DD/MM/YYYY')
                    : null
                }
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Nome do cartório" value={dadosCadastro.nomeCartorioCertidao} />
            </div>

            {(dadosCadastro.textoLivroCertidao || dadosCadastro.textoFolhaCertidao) && (
              <div className={styles.fieldGroupRow}>
                {dadosCadastro.textoLivroCertidao && (
                  <FieldGroup label="Número do livro" value={dadosCadastro.textoLivroCertidao} />
                )}
                {dadosCadastro.textoFolhaCertidao && (
                  <FieldGroup label="Número da folha" value={dadosCadastro.textoFolhaCertidao} />
                )}
              </div>
            )}

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Matrícula / Número do termo"
                value={dadosCadastro.textoMatriculaCertidao}
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Local de registro"
                value={formatarLocalRegistro()}
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="CPF" value={formatarCpf(dadosCadastro.numeroCpfPessoa)} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Documento de identidade (RG)" value={dadosCadastro.numeroIdentidade} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Complemento do RG" value={dadosCadastro.nomeComplementoIdentidade} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Data de emissão do RG"
                value={
                  dadosCadastro.dataEmissaoIdentidade
                    ? moment(dadosCadastro.dataEmissaoIdentidade).format('DD/MM/YYYY')
                    : null
                }
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Emissão do RG"
                value={montarEmissaoRG(
                  dadosCadastro.siglaOrgaoEmissor,
                  dadosCadastro.siglaUfIdentidade
                )}
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Carteira de Trabalho" value={dadosCadastro.numeroCtps} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Série da Carteira de Trabalho" value={dadosCadastro.numeroSerieCtps} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Data de emissão da Carteira de Trabalho"
                value={
                  dadosCadastro.dataEmissaoCtps
                    ? moment(dadosCadastro.dataEmissaoCtps).format('DD/MM/YYYY')
                    : null
                }
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="UF emissor da Carteira de Trabalho"
                value={dadosCadastro.ufEmissoraCtps}
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Título de Eleitor" value={dadosCadastro.numeroTituloEleitorPessoa} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Zona" value={dadosCadastro.numeroZonaTituloEleitorPessoa} />
              <FieldGroup label="Seção" value={dadosCadastro.numeroSecaoTituloEleitorPessoa} />
            </div>

          </div>
        </Accordion>
      )}
    </>
  );

  const cardTrabalhoRenda = (
    <>
      {trabalhoRenda && (
        <Accordion
          id="trabalho-renda"
          title="Trabalho e Renda"
          icon="fa-money-check-alt" 
          scroll
          scrollOffset={SCROLL_OFFSET_NAV_FIXED}
        >
          <div className={styles.cardContent}>
            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Trabalhou na semana anterior à entrevista"
                value={trabalhoRenda?.trabalhouSemanaAnteriorAEntrevista?.nome}
              />
            </div>

            {trabalhoRenda?.trabalhouSemanaAnteriorAEntrevista?.codigo === 2 && (
              <div className={styles.fieldGroupRow}>
                <FieldGroup
                  label="Estava afastado(a) de um trabalho na semana anterior à entrevista"
                  value={trabalhoRenda?.estavaAfastadoTrabalhoSemanaAnteriorEntrevista?.nome}
                />
              </div>
            )}

            {(trabalhoRenda?.trabalhouSemanaAnteriorAEntrevista?.codigo === 1 ||
              trabalhoRenda?.estavaAfastadoTrabalhoSemanaAnteriorEntrevista?.codigo === 1) && (
              <>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Trabalho principal é na agricultura, criação de animais, pesca ou coleta"
                    value={trabalhoRenda?.exerceAtividadeAgriculturaCriacaoAnimaisPescaOuColeta?.nome}
                  />
                </div>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Trabalho principal"
                    value={trabalhoRenda?.funcaoPrincipalNoTrabalho?.nome}
                  />
                </div>
              </>
            )}

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Remuneração de trabalho no mês anterior à entrevista"
                value={formatarValor(trabalhoRenda?.remuneracaoTrabalhoMesAnteriorEntrevista)}
                emptyValue="Não recebeu"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Teve trabalho remunerado nos últimos 12 meses anteriores à entrevista?"
                value={trabalhoRenda?.teveTrabalhoRemunerado12MesesAnterioresEntrevista?.nome}
              />
            </div>

            {trabalhoRenda?.teveTrabalhoRemunerado12MesesAnterioresEntrevista?.codigo === 1 && (
              <>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Quantos meses trabalhou nesse período?"
                    value={trabalhoRenda?.qtdMesesTrabalhadosNos12MesesAnterioresEntrevista}
                  />
                </div>

                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Remuneração bruta de todos os trabalhos nesse período"
                    value={formatarValor(
                      trabalhoRenda?.remuneracaoBrutaTodosOsTrabalhos12MesesAnterioresEntrevista
                    )}
                  />
                </div>
              </>
            )}

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Ajuda ou doação"
                value={formatarValor(trabalhoRenda?.valorRecebidoAjudaOuDoacao)}
                emptyValue="Não recebe"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Aposentadoria, pensão ou BPC/LOAS"
                value={formatarValor(trabalhoRenda?.valorAposentadoriaPensaoBpcLoas)}
                emptyValue="Não recebe"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Seguro-desemprego"
                value={formatarValor(trabalhoRenda?.valorSeguroDesemprego)}
                emptyValue="Não recebe"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Pensão alimentícia"
                value={formatarValor(trabalhoRenda?.valorPensaoAlimenticia)}
                emptyValue="Não recebe"
              />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Outras rendas"
                value={formatarValor(trabalhoRenda?.outrasRendas)}
                emptyValue="Não recebe"
              />
            </div>
          </div>
        </Accordion>
      )}
    </>
  );

  const cardEscolaridade = (
    <>
      {escolaridade && (
        <Accordion
          id="escolaridade"
          title="Escolaridade"
          icon="fa-school"
          scroll
          scrollOffset={SCROLL_OFFSET_NAV_FIXED}
        >
          <div className={styles.cardContent}>
            <div className={styles.fieldGroupRow}>
              <FieldGroup label="Sabe ler e escrever" value={escolaridade?.sabeLerEEscrever?.nome} />
            </div>

            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Frequenta escola ou creche"
                value={escolaridade?.frequentaEscolaOuCreche?.nome}
              />
            </div>

            {/* Exibir se for "Sim, rede pública" ou "Sim, rede particular" */}
            {(escolaridade?.frequentaEscolaOuCreche?.codigo === 1 ||
              escolaridade?.frequentaEscolaOuCreche?.codigo === 2) && (
              <>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Nome da escola ou creche"
                    value={escolaridade?.nomeDaEscolaOuCreche}
                  />
                </div>

                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Local da escola ou creche"
                    value={`${escolaridade?.municipioDaEscolaOuCreche} - ${escolaridade?.ufDaEscolaOuCreche}`}
                  />
                </div>

                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Código do INEP/MEC da escola ou creche"
                    value={escolaridade?.codigoInepeMecEscolaOuCreche}
                  />
                </div>

                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Curso que frequenta"
                    value={escolaridade?.cursoQueFrequenta?.nome}
                  />
                </div>

                {/*
                  Exibir se for
                  "Ensino Fundamental regular (duração 8 anos)",
                  "Ensino Fundamental regular (duração 9 anos)",
                  "Ensino Fundamental especial", "Ensino Médio regular" ou
                  "Ensino Médio especial"
                */}
                {(escolaridade?.cursoQueFrequenta?.codigo === 4 ||
                  escolaridade?.cursoQueFrequenta?.codigo === 5 ||
                  escolaridade?.cursoQueFrequenta?.codigo === 6 ||
                  escolaridade?.cursoQueFrequenta?.codigo === 7 ||
                  escolaridade?.cursoQueFrequenta?.codigo === 8) && (
                  <div className={styles.fieldGroupRow}>
                    <FieldGroup
                      label="Ano/série que frequenta"
                      value={escolaridade?.anoSerieQueFrequenta?.nome}
                    />
                  </div>
                )}
              </>
            )}

            {/* Exibir se for "Não, já frequentou" */}
            {escolaridade?.frequentaEscolaOuCreche?.codigo === 3 && (
              <>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Qual o curso mais elevado que frequentou, no qual concluiu pelo menos um série?"
                    value={escolaridade?.cursoMaisElevadoQueFrequentou?.nome}
                  />
                </div>

                {/*
                  Exibido caso o campo anterior possua o valor
                  "Ensino Fundamental 1ª a 4ª séries, Elementar (Primário), Primeira fase do 1º grau",
                  "Ensino Fundamental 5ª a 8ª séries, Médio 1º ciclo (Ginasial), Segunda fase do 1º grau",
                  "Ensino Fundamental (duração 9 anos)",
                  "Ensino Fundamental Especial",
                  "Ensino Médio, 2º grau, Médio 2º ciclo (Científico, Clássico, Técnico, Normal)"
                  ou "Ensino Médio Especial"
                */}
                {(escolaridade?.cursoMaisElevadoQueFrequentou?.codigo === 4 ||
                  escolaridade?.cursoMaisElevadoQueFrequentou?.codigo === 5 ||
                  escolaridade?.cursoMaisElevadoQueFrequentou?.codigo === 6 ||
                  escolaridade?.cursoMaisElevadoQueFrequentou?.codigo === 7 ||
                  escolaridade?.cursoMaisElevadoQueFrequentou?.codigo === 8 ||
                  escolaridade?.cursoMaisElevadoQueFrequentou?.codigo === 9) && (
                  <>
                    <div className={styles.fieldGroupRow}>
                      <FieldGroup
                        label="Qual o último ano/série que concluiu com aprovação nesse curso que frequentou?"
                        value={escolaridade?.ultimoAnoSerieNoCursoQueFrequentou?.nome}
                      />
                    </div>
                    <div className={styles.fieldGroupRow}>
                      <FieldGroup
                        label="Concluiu esse curso que frequentou?"
                        value={escolaridade?.concluiuOCursoQueFrequentou?.nome}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Accordion>
      )}
    </>
  );

  const cardPessoaComDeficiencia = (
    <>
      {deficiencia && (
        <Accordion
          id="pessoa-com-deficiencia"
          title="Pessoa com Deficiência"
          icon="fa-wheelchair"
          scroll
          scrollOffset={SCROLL_OFFSET_NAV_FIXED}
        >
          <div className={styles.cardContent}>
            <div className={styles.fieldGroupRow}>
              <FieldGroup
                label="Possui alguma deficiência permanente que limite as suas atividades habituais (como trabalhar, ir à escola, brincar, etc.)"
                value={deficiencia?.pessoaTemDeficiencia?.nome}
              />
            </div>

            {deficiencia?.pessoaTemDeficiencia?.codigo === 1 && (
              <>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup label="Tipo de deficiência" value={deficiencia?.deficiencias} />
                </div>
                <div className={styles.fieldGroupRow}>
                  <FieldGroup
                    label="Recebe cuidados permanentes de terceiros?"
                    value={deficiencia?.cuidadosPermanentesTerceiros}
                  />
                </div>
              </>
            )}
          </div>
        </Accordion>
      )}
    </>
  );

  const cardIdentificacaoRF = rf => (
    <Accordion
      id="identificacao"
      title="Identificação"
      icon="fa-id-badge"
      alwaysOpen
    >
      <div className={styles.cardContent}>

        <div className={styles.fieldGroupRow}>
          <FieldGroup
            label="Estado cadastral"
            value={rf.tipoEstadoCadastralMembro?.nome}
            size="sm"
          />
          <FieldGroup
            label="NIS, PIS ou PASEP"
            value={
              rf.numeroNisPisPasepPessoa
                ? formatarNit(rf.numeroNisPisPasepPessoa)
                : ''
            }
          />
        </div>

        <div className={styles.fieldGroupRow}>
          <FieldGroup
            label="Data de nascimento"
            value={
              rf.dataNascimentoPessoa
                ? moment(rf.dataNascimentoPessoa).format('DD/MM/YYYY')
                : null
            }
          />
          <FieldGroup label="Sexo" value={rf.tipoSexoPessoa?.nome} />
        </div>

        <div className={styles.fieldGroupRow}>
          <FieldGroup
            label="Filiação 1 (ou nome completo da mãe)"
            value={rf.filiacao1}
            emptyValue="Não sabe"
          />
        </div>
      </div>
    </Accordion>
  );

  const cardContatoRF = rf => (
    <Accordion
      id="contato"
      title="Telefone e e-mail"
      icon="fa-id-card"
      alwaysOpen
    >
      <div className={styles.cardContent}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FieldGroup
              label="Telefone Primário"
              value={mostrarTelefoneRF(rf.dddContato1, rf.telefoneContato1)}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldGroup
              label="Telefone Secundário"
              value={mostrarTelefoneRF(rf.dddContato2, rf.telefoneContato2)}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldGroup label="E-mail" value={rf.email} />
          </Grid>
        </Grid>
      </div>
    </Accordion>
  );

  const responsavelFamiliar = (
    <>
      {responsaveisFamiliares.map((rf, index) => (
        <React.Fragment key={index}>
          <Card>
            <div className={styles.responsavelFamiliar}>
              <i className={`fas fa-id-card-alt ${styles.icon}`} />
              <div className="flex-column">
                <span className={styles.nomeResponsavel}>{(rf?.temIdentidadeGenero === 1 && rf?.nomeSocialApelidoPessoa) ? rf?.nomeSocialApelidoPessoa : rf?.nomePessoa}</span>
                {perfil && perfil.nome.toUpperCase() === rf.nomePessoa.toUpperCase() && (
                  <span className="fw500">Você é responsável pela família</span>
                )}
                {perfil && perfil.nome.toUpperCase() !== rf.nomePessoa.toUpperCase() && (
                  <span className="fw500">É responsável pela unidade familiar</span>
                )}
              </div>
            </div>
          </Card>
          {cardIdentificacaoRF(rf)}
          {cardContatoRF(rf)}
        </React.Fragment>
      ))}
    </>
  );

  const cardIdentificacaoRL = (rl) => {
    const dadosCadastro = rl.pessoaDadosCadastroDTO;
    return (
      <Accordion
        id="identificacao"
        title="Identificação"
        icon="fa-id-badge"
        alwaysOpen
      >
        <div className={styles.cardContent}>
          <div className={styles.fieldGroupRow}>
            <FieldGroup
              label="NIS, PIS ou PASEP"
              value={
                dadosCadastro.numeroNisPisPasepPessoa
                  ? formatarNit(dadosCadastro.numeroNisPisPasepPessoa)
                  : ''
              }
            />
            <FieldGroup
              label="Data de nascimento"
              value={
                dadosCadastro.dataNascimentoPessoa
                  ? moment(dadosCadastro.dataNascimentoPessoa).format('DD/MM/YYYY')
                  : null
              }
            />
          </div>

          <div className={styles.fieldGroupRow}>
            <FieldGroup label="Sexo" value={dadosCadastro.tipoSexoPessoa?.nome} />
            <FieldGroup label="Representação legal" value={rl.representacaoLegal?.nome} />
          </div>

          <div className={styles.fieldGroupRow}>
            <FieldGroup
              label="Filiação 1 (ou nome completo da mãe)"
              value={dadosCadastro.filiacao1}
              emptyValue="Não sabe"
            />
          </div>
        </div>
      </Accordion>
    );
  };

  const cardContatoRL = (rl) => {
    return (
      <Accordion
        id="contato"
        title="Endereço, telefone e e-mail"
        icon="fa-id-card"
        alwaysOpen
      >
        <div className={styles.cardContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldGroup
                label="Logradouro (tipo, título, nome)"
                value={`${rl.tipoLogradouro ? `${rl.tipoLogradouro + ' '}` : ''}${
                  rl.nomeLogradouro
                }`}
              />
            </Grid>
            <Grid item xs={6}>
              <FieldGroup label="Número" value={rl.numeroLogradouro} />
            </Grid>
            <Grid item xs={6}>
              <FieldGroup label="Complemento" value={rl.complementoLogradouro} />
            </Grid>
            <Grid item xs={12}>
              <FieldGroup label="Município" value={rl.nomeMunicipio} />
            </Grid>
            <Grid item xs={6}>
              <FieldGroup label="UF" value={rl.siglaUf} />
            </Grid>
            <Grid item xs={6}>
              <FieldGroup label="CEP" value={formatarCep(rl.cepLogradouro)} />
            </Grid>
            <Grid item xs={6}>
              <FieldGroup
                label="Telefone"
                value={mostrarTelefoneRL(rl)}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldGroup label="E-mail" value={rl.email} />
            </Grid>
          </Grid>
        </div>
      </Accordion>
    );
  };

  const representanteLegalComponent = () => {
    const dadosCadastroRL = representanteLegal.pessoaDadosCadastroDTO;
    return (
      <>
        <Card>
          <div className={styles.responsavelFamiliar}>
            <i className={`fas fa-user-tie ${styles.icon}`} />
            <div className="flex-column">
              <span className={styles.nomeResponsavel}>{dadosCadastroRL.nomePessoa}</span>
              {perfil && perfil.nome.toUpperCase() === dadosCadastroRL.nomePessoa.toUpperCase() && (
                <span className="fw500">
                  Você é {dadosCadastroRL.tipoSexoPessoa && (dadosCadastroRL.tipoSexoPessoa?.codigo === 1 ? 'o ' : 'a ')}Representante Legal
                  da família
                </span>
              )}
              {perfil && perfil.nome.toUpperCase() !== dadosCadastroRL.nomePessoa.toUpperCase() && (
                <span className="fw500">
                  É {dadosCadastroRL.tipoSexoPessoa && (dadosCadastroRL.tipoSexoPessoa?.codigo === 1 ? 'o ' : 'a ')}Representante Legal da
                  família
                </span>
              )}
            </div>
          </div>
          {indicacaoRLDeObito() && (
            <>
              <Message
                type={'error'}
                title={`ATENÇÃO!`}
                text={mensagemObitoRL()}
                textBold={true}
                className={styles.message}
              />
              <p />
            </>
          )}
        </Card>
        {cardIdentificacaoRL(representanteLegal)}
        {cardContatoRL(representanteLegal)}
      </>
    )
  };

  const membroFamiliar = (
    <>
      <Card>
        <div className={styles.responsavelFamiliar}>
          <i className={`fas fa-user-friends ${styles.icon}`} />
          <div className="flex-column">
            <span className={styles.nomeResponsavel}>{(dadosCadastro?.temIdentidadeGenero === 1 && dadosCadastro?.nomeSocialApelidoPessoa) ? dadosCadastro?.nomeSocialApelidoPessoa : dadosCadastro?.nomePessoa}</span>
            <span className="fw500">{mostrarParentesco()}</span>
          </div>
        </div>
      </Card>
      {cardIdentificacao}
      {cardDocumentos}
      {cardPessoaComDeficiencia}
      {cardEscolaridade}
      {cardTrabalhoRenda}
    </>
  );

  return (
    <>
      {perfil && (
        <>
          {view === DetalhesView.MEMBRO && pessoa && membroFamiliar}
          {view === DetalhesView.RESPONSAVEL &&
            responsaveisFamiliares &&
            responsaveisFamiliares.length > 0 &&
            responsavelFamiliar}
          {view === DetalhesView.REPRESENTANTE_LEGAL &&
            representanteLegal &&
            representanteLegalComponent()}
        </>
      )}
    </>
  );
};

export default DetalhesMembro;
